import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { LicitacionFormStep1 } from "./licitacion-form-step1";
import { LicitacionFormStep2 } from "./licitacion-form-step2";
import { LicitacionFormStep3 } from "./licitacion-form-step3";
import { ChevronLeft, ChevronRight, Save, Loader2 } from "lucide-react";

interface FormData {
  titulo: string;
  referencia: string;
  descripcion: string;
  organismo: string;
  tipo_contrato: string;
  // Mejora D (changelog cliente): equipo dueño de la licitación.
  team_id: string;
  presupuesto_base: string;
  valor_estimado: string;
  fecha_publicacion: string;
  fecha_limite: string;
  prioridad: string;
  url_plataforma: string;
  etiquetas: string[];
  notas: string;
}

const INITIAL_DATA: FormData = {
  titulo: "",
  referencia: "",
  descripcion: "",
  organismo: "",
  tipo_contrato: "",
  team_id: "",
  presupuesto_base: "",
  valor_estimado: "",
  fecha_publicacion: "",
  fecha_limite: "",
  prioridad: "MEDIA",
  url_plataforma: "",
  etiquetas: [],
  notas: "",
};

const STEPS = [
  { title: "Información básica", description: "Datos generales de la licitación" },
  { title: "Detalles económicos", description: "Presupuesto, fechas y prioridad" },
  { title: "Etiquetas y notas", description: "Clasificación y notas internas" },
];

interface LicitacionFormWizardProps {
  initialData?: Partial<FormData>;
  licitacionId?: string;
}

export function LicitacionFormWizard({ initialData, licitacionId }: LicitacionFormWizardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!licitacionId;

  const [step, setStep] = useState(0);
  const [data, setData] = useState<FormData>({ ...INITIAL_DATA, ...initialData });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const payload = {
        titulo: formData.titulo,
        referencia: formData.referencia || null,
        descripcion: formData.descripcion || null,
        organismo: formData.organismo,
        tipo_contrato: formData.tipo_contrato,
        // El team_id solo se envía en creación; el endpoint PUT no lo
        // acepta (la licitación se queda en el team original).
        ...(isEdit ? {} : { team_id: formData.team_id }),
        presupuesto_base: formData.presupuesto_base ? parseFloat(formData.presupuesto_base) : null,
        valor_estimado: formData.valor_estimado ? parseFloat(formData.valor_estimado) : null,
        fecha_publicacion: formData.fecha_publicacion || null,
        fecha_limite: formData.fecha_limite || null,
        prioridad: formData.prioridad,
        url_plataforma: formData.url_plataforma || null,
        etiquetas: formData.etiquetas,
        notas: formData.notas || null,
      };

      if (isEdit) {
        return fetchWithAuth(`/api/v1/licitaciones/${licitacionId}`, { method: "PUT", body: JSON.stringify(payload) });
      }
      return fetchWithAuth("/api/v1/licitaciones", { method: "POST", body: JSON.stringify(payload) });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["licitaciones"] });
      if (isEdit) {
        queryClient.invalidateQueries({ queryKey: ["licitacion", licitacionId] });
      }
      navigate(isEdit ? `/licitaciones/${licitacionId}` : `/licitaciones/${result.id}`);
    },
  });

  const updateData = (partial: Partial<FormData>) => {
    setData((prev) => ({ ...prev, ...partial }));
    const newErrors = { ...errors };
    Object.keys(partial).forEach((k) => delete newErrors[k]);
    setErrors(newErrors);
  };

  const validateStep = (stepIndex: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (stepIndex === 0) {
      if (!data.titulo.trim()) newErrors.titulo = "El título es obligatorio";
      if (!data.organismo.trim()) newErrors.organismo = "El organismo es obligatorio";
      if (!data.tipo_contrato) newErrors.tipo_contrato = "Selecciona un tipo de contrato";
      // Mejora D: equipo obligatorio al crear (no se valida en edición porque
      // el campo se oculta en ese modo).
      if (!isEdit && !data.team_id) newErrors.team_id = "Selecciona un equipo";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handlePrev = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = () => {
    if (validateStep(step)) mutation.mutate(data);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicators */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={s.title} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              i <= step ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
            }`}>
              {i + 1}
            </div>
            <div className="ml-2 hidden sm:block">
              <p className={`text-sm font-medium ${i <= step ? "text-gray-900" : "text-gray-500"}`}>{s.title}</p>
            </div>
            {i < STEPS.length - 1 && <div className={`mx-4 h-px w-12 ${i < step ? "bg-blue-600" : "bg-gray-200"}`} />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">{STEPS[step].title}</h2>
        <p className="text-sm text-gray-500 mb-6">{STEPS[step].description}</p>

        {step === 0 && (
          <LicitacionFormStep1
            data={data}
            onChange={updateData}
            errors={errors}
            hideTeamSelector={isEdit}
          />
        )}
        {step === 1 && <LicitacionFormStep2 data={data} onChange={updateData} errors={errors} />}
        {step === 2 && <LicitacionFormStep3 data={data} onChange={updateData} errors={errors} />}

        {mutation.isError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {mutation.error instanceof Error ? mutation.error.message : "Error al guardar"}
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between mt-6">
        <button
          type="button"
          onClick={step === 0 ? () => navigate(-1) : handlePrev}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {step === 0 ? "Cancelar" : "Anterior"}
        </button>

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={handleNext}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Siguiente
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Guardar cambios" : "Crear licitación"}
          </button>
        )}
      </div>
    </div>
  );
}
